<template>
  <div class="price-check-page">
    <div class="page-header">
      <div>
        <h3>AI Booking Re-shopping Engine</h3>
      </div>
      <div class="page-actions">
        <b-button
          variant="outline-danger"
          class="action-button action-button-danger"
          :disabled="isMaintenanceLoading"
          @click="openMaintenanceModal('truncate')"
        >
          <b-icon icon="trash" />
          Truncate Bookings
        </b-button>
        <b-button
          variant="outline-danger"
          class="action-button action-button-danger"
          :disabled="isMaintenanceLoading"
          @click="openMaintenanceModal('truncateLogs')"
        >
          <b-icon icon="journal-x" />
          Truncate Re-shopping Logs
        </b-button>
        <b-button
          variant="outline-primary"
          class="action-button action-button-secondary"
          :disabled="isMaintenanceLoading"
          @click="openMaintenanceModal('import')"
        >
          <b-icon icon="cloud-arrow-down" />
          Import Bookings
        </b-button>
        <b-button
          variant="primary"
          class="action-button action-button-primary"
          :disabled="isMaintenanceLoading || isExecutionActive"
          @click="openMaintenanceModal('execute')"
        >
          <b-icon icon="play-circle" />
          {{ isExecutionActive ? 'Script Running...' : 'Execute Re-shopping Script' }}
        </b-button>
      </div>
    </div>

    <b-modal
      v-model="showMaintenanceModal"
      centered
      hide-header-close
      hide-header
      body-class="maintenance-modal-body"
      footer-class="maintenance-modal-footer"
      @hidden="resetMaintenanceModal"
    >
      <div class="maintenance-confirm">
        <div :class="['maintenance-icon', selectedMaintenanceAction.tone || 'primary']">
          <b-icon :icon="selectedMaintenanceAction.icon || 'question-circle'" />
        </div>
        <div class="maintenance-copy">
          <span>{{ selectedMaintenanceAction.kicker || 'Confirmation Required' }}</span>
          <h4>{{ maintenanceModalTitle }}</h4>
          <p>{{ maintenanceModalMessage }}</p>
        </div>
      </div>
      <div v-if="showMaintenanceProgress" class="maintenance-progress">
        <div class="maintenance-progress-header">
          <strong>{{ maintenanceProgressTitle }}</strong>
          <span>{{ maintenanceProgressLabel }}</span>
        </div>
        <div class="maintenance-progress-track">
          <div class="maintenance-progress-bar"></div>
        </div>
      </div>
      <template #modal-footer>
        <b-button
          class="modal-cancel-button"
          variant="light"
          :disabled="isMaintenanceLoading"
          @click="showMaintenanceModal = false"
        >
          No
        </b-button>
        <b-button
          :variant="selectedMaintenanceAction.confirmVariant || 'primary'"
          class="modal-confirm-button"
          :disabled="isMaintenanceLoading"
          @click="confirmMaintenanceAction"
        >
          {{ isMaintenanceLoading ? 'Processing...' : 'Yes' }}
        </b-button>
      </template>
    </b-modal>

    <section
      v-if="executionId || executionProgress"
      :class="['execution-progress-panel', executionStatusClass]"
    >
      <div class="execution-progress-header">
        <div>
          <span class="execution-kicker">Live execution</span>
          <h4>Price Check Execution Progress</h4>
        </div>
        <span class="execution-status-chip">{{ executionStatusLabel }}</span>
      </div>

      <div class="execution-progress-main">
        <div class="execution-progress-copy">
          <strong>{{ executionPercentage }}%</strong>
          <span>{{ executionProcessedLabel }}</span>
        </div>
        <div class="execution-progress-track" role="progressbar" :aria-valuenow="executionPercentage" aria-valuemin="0" aria-valuemax="100">
          <div class="execution-progress-fill" :style="{ width: `${executionPercentage}%` }"></div>
        </div>
      </div>

      <div class="execution-metrics-grid">
        <div
          v-for="metric in executionMetrics"
          :key="metric.label"
          class="execution-metric"
        >
          <span>{{ metric.label }}</span>
          <strong>{{ metric.value }}</strong>
        </div>
      </div>

      <div v-if="executionClientProgress.length" class="execution-clients">
        <h5>Client-wise Progress</h5>
        <div v-for="client in executionClientProgress" :key="client.name" class="execution-client">
          <div class="execution-progress-copy">
            <strong>{{ client.name }}</strong>
            <span>{{ formatInteger(client.processed) }} of {{ formatInteger(client.total) }} bookings processed · {{ client.percentage }}%</span>
          </div>
          <div class="execution-progress-track" role="progressbar" :aria-label="`${client.name} booking progress`" :aria-valuenow="client.percentage" aria-valuemin="0" aria-valuemax="100">
            <div class="execution-progress-fill" :style="{ width: `${client.percentage}%` }"></div>
          </div>
          <div class="execution-client-counts">
            Successful: {{ formatInteger(client.successful) }} · Failed: {{ formatInteger(client.failed) }} · Remaining: {{ formatInteger(client.remaining) }}
          </div>
        </div>
      </div>

      <div v-if="executionErrorMessage" class="execution-error">
        {{ executionErrorMessage }}
      </div>
    </section>

    <div class="filter-panel">
      <div class="filter-grid">
        <div class="filter-field">
          <label>Client</label>
          <select
            v-model="filters.client"
            :disabled="isLoading || isClientOptionsLoading"
          >
            <option value="">All Clients</option>
            <option
              v-for="client in clientOptions"
              :key="client"
              :value="client"
            >
              {{ client }}
            </option>
          </select>
        </div>
        <div class="filter-field">
          <label>Booking Id</label>
          <input
            v-model.trim="filters.bookingId"
            type="text"
            placeholder="Search booking id"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="filter-field">
          <label>City</label>
          <input
            v-model.trim="filters.city"
            type="text"
            placeholder="Search city"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="filter-field">
          <label>Country</label>
          <input
            v-model.trim="filters.country"
            type="text"
            placeholder="Search country"
            @keyup.enter="applyFilters"
          />
        </div>
        <div
          v-if="!showMoreFilters"
          class="filter-toggle"
        >
          <b-button
            variant="outline-primary"
            :disabled="isLoading"
            @click="showMoreFilters = !showMoreFilters"
          >
            <b-icon :icon="showMoreFilters ? 'chevron-up' : 'chevron-down'" />
            {{ showMoreFilters ? 'Less Filters' : 'More Filters' }}
          </b-button>
        </div>
        <div
          v-show="showMoreFilters"
          class="filter-field"
        >
          <label>Booking Date</label>
          <input
            v-model="filters.bookingDate"
            type="date"
          />
        </div>
        <div
          v-show="showMoreFilters"
          class="filter-field"
        >
          <label>Rebooking Status</label>
          <select
            v-model="filters.alternativePriceStatus"
            @keyup.enter="applyFilters"
          >
            <option
              v-for="option in rebookingStatusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div
          v-show="showMoreFilters"
          class="filter-field"
        >
          <label>Profit Range</label>
          <select
            v-model="filters.profitRange"
            @keyup.enter="applyFilters"
          >
            <option
              v-for="option in profitRangeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div
          v-show="showMoreFilters"
          class="filter-field"
        >
          <label>Refundability</label>
          <select
            v-model="filters.refundability"
            @keyup.enter="applyFilters"
          >
            <option
              v-for="option in refundabilityOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div
          class="filter-actions"
          :class="{ 'filter-actions-expanded': showMoreFilters }"
        >
          <b-button variant="primary" :disabled="isLoading" @click="applyFilters">Submit</b-button>
          <b-button variant="outline-primary" :disabled="isLoading" @click="resetFilters">Reset</b-button>
          <b-button
            v-if="showMoreFilters"
            variant="outline-primary"
            :disabled="isLoading"
            @click="showMoreFilters = false"
          >
            <b-icon icon="chevron-up" />
            Less Filters
          </b-button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="initial-loader">
      <div class="loader-ring"></div>
      <h5>Loading bookings</h5>
      <p>Please wait while the latest booking data is fetched.</p>
    </div>

    <div v-else>
      <div class="booking-panel">
        <div v-if="isLoading" class="panel-loader">
          <div class="loader-ring small"></div>
          <span>Updating results...</span>
        </div>
        <div class="table-tools">
          <div class="result-summary">
            <div class="result-count">
              <strong>{{ totalRecords }}</strong>
              <span>bookings found</span>
            </div>
            <b-button
              size="sm"
              variant="primary"
              class="reports-button"
              @click="goToReports"
            >
              Reports
            </b-button>
          </div>
          <div class="page-size-control">
            <label>Rows</label>
            <b-form-select v-model.number="pageSize" :disabled="isLoading" @change="changePageSize">
              <b-form-select-option :value="10">10</b-form-select-option>
              <b-form-select-option :value="25">25</b-form-select-option>
              <b-form-select-option :value="50">50</b-form-select-option>
              <b-form-select-option :value="100">100</b-form-select-option>
              <b-form-select-option :value="200">200</b-form-select-option>
            </b-form-select>
          </div>
        </div>

        <h2 class="text-center p-5" v-if="nodata">No Data Found</h2>

        <div v-else class="pagination mt-3">
          <a href="#" @click.prevent="firstPage" :class="{ disabled: currentPage === 1 || isLoading }">First</a>
          <a href="#" @click.prevent="previousPage" :class="{ disabled: currentPage === 1 || isLoading }">&laquo;</a>
          <a
            v-for="page in visiblePages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click.prevent="goToPage(page)"
          >
            {{ page }}
          </a>
          <a href="#" @click.prevent="nextPage" :class="{ disabled: currentPage === totalPages || isLoading }">&raquo;</a>
          <a href="#" @click.prevent="lastPage" :class="{ disabled: currentPage === totalPages || isLoading }">Last</a>
        </div>

        <div v-if="!nodata" class="table-wrap" :class="{ refreshing: isLoading }">
          <table class="booking-table">
            <thead>
              <tr>
                <!-- <th>Client</th> -->
                <th>Booking Id</th>
                <th>Hotel Name</th>
                <th>Location</th>
                <th>CheckIn</th>
                <th>Price</th>
                <th>Supplier</th>
                <th>Next Check</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="booking in bookings"
                :key="booking.BookingId"
                :class="{ 'cheap-price-row': isCheapPriceDetected(booking) }"
              >
                <!-- <td>{{ booking.Client }}</td> -->
                <td class="booking-id">
                  <span>{{ booking.BookingId }}</span>
                  <b-icon
                    :id="`room-details-${booking.BookingId}`"
                    icon="info-circle"
                    class="room-info-icon"
                  />
                  <b-popover
                    :target="`room-details-${booking.BookingId}`"
                    triggers="hover"
                    placement="right"
                    custom-class="room-details-popover"
                  >
                    <template #title>
                      Room Details
                    </template>
                    <div
                      v-if="getRoomDetails(booking).length"
                      class="room-details-list"
                    >
                      <div
                        v-for="(room, roomIndex) in getRoomDetails(booking)"
                        :key="roomIndex"
                        class="room-details-item"
                      >
                        <strong>{{ roomTitle(room, roomIndex) }}</strong>
                        <span v-if="room.RoomCategory">{{ room.RoomCategory }}</span>
                        <small v-if="room.Room_TypeDescription">{{ room.Room_TypeDescription }}</small>
                        <ul v-if="getPassengers(room).length">
                          <li
                            v-for="(passenger, passengerIndex) in getPassengers(room)"
                            :key="passengerIndex"
                          >
                            {{ passengerText(passenger) }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <span v-else>No room details available.</span>
                  </b-popover>
                </td>
                <td>{{ booking.Name }}</td>
                <td>{{ booking.City }} <span>({{ booking.Country }})</span></td>
                <td>
                  <span
                    :id="`checkin-date-${booking.BookingId}`"
                    class="date-popover-target"
                  >
                    {{ formatDisplayDate(booking.Date) }}
                  </span>
                  <b-popover
                    :target="`checkin-date-${booking.BookingId}`"
                    triggers="hover focus"
                    placement="top"
                    custom-class="checkin-days-popover"
                  >
                    <template #title>
                      Day Till Checkin
                    </template>
                    {{ formatDayTillCheckin(booking.Day_Till_Checkin) }}
                  </b-popover>
                </td>
                <td class="price-cell">{{ booking.Price }}</td>
                <td>{{ booking.Supplier }}</td>
                <td>
                  <span class="next-check-value" :title="formatExactNextCheck(booking.Next_Check)">
                    {{ formatRelativeNextCheck(booking.Next_Check) }}
                  </span>
                </td>
                <td>
                  <b-button
                    size="sm"
                    variant="primary"
                    class="check-btn"
                    :disabled="isLoading || checkingBookingId === String(booking.BookingId)"
                    @click="checkBooking(booking)"
                  >
                    {{ checkingBookingId === String(booking.BookingId) ? 'Loading...' : 'Details' }}
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!nodata" class="pagination">
          <a href="#" @click.prevent="firstPage" :class="{ disabled: currentPage === 1 || isLoading }">First</a>
          <a href="#" @click.prevent="previousPage" :class="{ disabled: currentPage === 1 || isLoading }">&laquo;</a>
          <a
            v-for="page in visiblePages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click.prevent="goToPage(page)"
          >
            {{ page }}
          </a>
          <a href="#" @click.prevent="nextPage" :class="{ disabled: currentPage === totalPages || isLoading }">&raquo;</a>
          <a href="#" @click.prevent="lastPage" :class="{ disabled: currentPage === totalPages || isLoading }">Last</a>
        </div>

        <div v-if="!nodata" class="pagination-container">
          <input
            type="number"
            v-model.number="goToPageInput"
            class="mx-2 hhsna"
            :min="1"
            :max="totalPages"
            placeholder="Page #"
          />
          <p class="parabase">out of {{ totalPages }}</p>
          <b-button
            variant="success"
            class="hsggaf"
            @click="goToSpecificPage"
            :disabled="isLoading || !isValidPage(goToPageInput)"
          >
            Go
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import {
  BButton,
  BIcon,
  BFormSelect,
  BFormSelectOption,
  BPopover,
  BModal,
} from 'bootstrap-vue'

const EXECUTION_STORAGE_KEY = 'priceCheckActiveExecutionId'

export default {
  components: {
    BButton,
    BIcon,
    BFormSelect,
    BFormSelectOption,
    BPopover,
    BModal,
  },
  data() {
    return {
      loading: true,
      isLoading: false,
      nodata: false,
      bookings: [],
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      totalPages: 1,
      goToPageInput: null,
      checkingBookingId: '',
      isMaintenanceLoading: false,
      isClientOptionsLoading: false,
      executionId: '',
      executionProgress: null,
      executionStatus: '',
      executionPollTimer: null,
      executionRequestToken: 0,
      isExecutionPolling: false,
      executionCompletionNotified: false,
      executionProgressError: '',
      maintenanceStartedAt: null,
      showMaintenanceModal: false,
      maintenanceAction: '',
      showMoreFilters: false,
      clientOptions: [],
      filters: {
        bookingId: '',
        city: '',
        country: '',
        client: '',
        alternativePriceStatus: '',
        bookingDate: '',
        profitRange: '',
        refundability: '',
      },
      rebookingStatusOptions: [
        { label: 'All', value: '' },
        { label: 'Cheaper Found', value: 'cheaper_found' },
        { label: 'No Cheaper Found', value: 'no_cheaper_found' },
      ],
      profitRangeOptions: [
        { label: 'All Profit Ranges', value: '' },
        { label: '0-20', value: '0-20' },
        { label: '20-50', value: '20-50' },
        { label: '50-100', value: '50-100' },
        { label: '100+', value: '100+' },
      ],
      refundabilityOptions: [
        { label: 'All', value: '' },
        { label: 'Refundable', value: 'refundable' },
        { label: 'Non Refundable', value: 'non_refundable' }
      ],
    }
  },
  computed: {
    maintenanceActions() {
      return {
        truncate: {
          label: 'truncate bookings',
          title: 'Truncate Bookings',
          endpoint: 'price-check/bookings/truncate',
          icon: 'trash',
          tone: 'danger',
          confirmVariant: 'danger',
          kicker: 'Destructive Action',
        },
        truncateLogs: {
          label: 'truncate logs',
          title: 'Truncate Logs',
          endpoint: 'price-check/bookings/logs/truncate',
          icon: 'trash',
          tone: 'danger',
          confirmVariant: 'danger',
          kicker: 'Destructive Action',
        },
        import: {
          label: 'import',
          title: 'Import Bookings',
          endpoint: 'price-check/bookings/import',
          icon: 'cloud-upload',
          tone: 'primary',
          confirmVariant: 'primary',
          kicker: 'Data Import',
        },
        execute: {
          label: 'execute script',
          title: 'Execute Script',
          endpoint: 'price-check/bookings/execute-script',
          icon: 'play-circle',
          tone: 'success',
          confirmVariant: 'success',
          kicker: 'Run Script',
        },
      }
    },
    selectedMaintenanceAction() {
      return this.maintenanceActions[this.maintenanceAction] || {}
    },
    maintenanceModalTitle() {
      return this.selectedMaintenanceAction.title || 'Confirm Action'
    },
    maintenanceModalMessage() {
      const label = this.selectedMaintenanceAction.label || 'continue'
      return `Are you sure you want to ${label}? This action will run immediately after confirmation.`
    },
    showMaintenanceProgress() {
      return this.isMaintenanceLoading && this.maintenanceAction === 'execute'
    },
    maintenanceProgressTitle() {
      return this.maintenanceStartedAt ? 'Starting re-shopping script' : 'Preparing request'
    },
    maintenanceProgressLabel() {
      return this.maintenanceStartedAt ? 'Please wait while the server starts the job.' : 'Waiting to start.'
    },
    normalizedExecutionStatus() {
      const progress = this.executionProgress || {}
      return String(progress.execution_status || this.executionStatus || '').trim().toLowerCase()
    },
    isExecutionFinished() {
      return this.isTerminalExecution(this.executionProgress)
    },
    isExecutionActive() {
      return Boolean(this.executionId) && !this.isExecutionFinished
    },
    executionStatusLabel() {
      if (!this.executionId && !this.executionProgress) {
        return 'Waiting'
      }
      return this.formatStatusLabel(this.normalizedExecutionStatus || 'starting')
    },
    executionStatusClass() {
      const status = this.normalizedExecutionStatus
      if (status === 'failed') {
        return 'status-failed'
      }
      if (status === 'completed_with_errors') {
        return 'status-warning'
      }
      if (status === 'completed') {
        return 'status-completed'
      }
      return 'status-running'
    },
    executionPercentage() {
      const progress = this.executionProgress || {}
      return this.clampPercentage(progress.progress_percentage)
    },
    executionProcessedLabel() {
      const progress = this.executionProgress || {}
      const processed = this.getCompletedBookings(progress)
      const total = this.toFiniteNumber(progress.total_bookings)
      if (total > 0 || processed > 0) {
        return `${this.formatInteger(processed)} of ${this.formatInteger(total)} bookings processed`
      }
      return 'Waiting for booking progress...'
    },
    executionMetrics() {
      const progress = this.executionProgress || {}
      return [
        { label: 'Total Bookings', value: this.formatInteger(progress.total_bookings) },
        { label: 'Processed Bookings', value: this.formatInteger(this.getCompletedBookings(progress)) },
        { label: 'Successful Bookings', value: this.formatInteger(progress.successful_bookings) },
        { label: 'Failed Bookings', value: this.formatInteger(progress.failed_bookings) },
        { label: 'Remaining Bookings', value: this.formatInteger(progress.remaining_bookings) },
        { label: 'Current Batch', value: this.formatInteger(progress.current_batch) },
      ]
    },
    executionClientProgress() {
      const clients = (this.executionProgress || {}).clients || {}
      return Object.entries(clients).map(([name, value]) => {
        const progress = value || {}
        const total = Math.max(0, this.toFiniteNumber(progress.total_bookings ?? progress.total))
        const successful = Math.max(0, this.toFiniteNumber(progress.successful_bookings ?? progress.successful ?? progress.success))
        const failed = Math.max(0, this.toFiniteNumber(progress.failed_bookings ?? progress.failed))
        const processed = Math.max(0, this.toFiniteNumber(
          progress.completed_bookings ?? progress.processed_bookings ?? progress.processed ?? (successful + failed)
        ))
        return {
          name,
          total,
          processed,
          successful,
          failed,
          remaining: Math.max(0, total - processed),
          percentage: this.clampPercentage(total > 0 ? processed / total * 100 : 0),
        }
      })
    },
    executionErrorMessage() {
      const progress = this.executionProgress || {}
      return progress.last_error || this.executionProgressError || ''
    },
    visiblePages() {
      const pages = []
      const startPage = Math.max(1, this.currentPage - 2)
      const endPage = Math.min(this.totalPages, this.currentPage + 2)
      for (let page = startPage; page <= endPage; page++) {
        pages.push(page)
      }
      return pages
    },
  },
  mounted() {
    this.fetchClientOptions()
    this.fetchBookings()
    this.resumeExecutionProgress()
  },
  beforeDestroy() {
    this.executionRequestToken += 1
    this.stopExecutionProgressPolling()
  },
  methods: {
    fetchClientOptions() {
      this.isClientOptionsLoading = true
      axios.get('price-check/bookings/clients')
        .then(response => {
          this.clientOptions = Array.isArray(response.data.clients) ? response.data.clients : []
        })
        .catch(error => {
          console.error('Error fetching booking clients:', error)
          this.clientOptions = []
        })
        .finally(() => {
          this.isClientOptionsLoading = false
        })
    },
    openMaintenanceModal(action) {
      this.maintenanceAction = action
      this.showMaintenanceModal = true
    },
    resetMaintenanceModal() {
      if (!this.isMaintenanceLoading) {
        this.maintenanceAction = ''
        this.maintenanceStartedAt = null
      }
    },
    confirmMaintenanceAction() {
      const action = this.selectedMaintenanceAction
      if (!action.endpoint || this.isMaintenanceLoading) {
        return
      }

      const activeAction = this.maintenanceAction
      this.isMaintenanceLoading = true
      this.maintenanceStartedAt = new Date()
      axios.post(action.endpoint)
        .then(response => {
          const responseData = response.data || {}
          const message = response.data && response.data.message
            ? response.data.message
            : 'Action completed successfully.'
          if (activeAction === 'execute') {
            this.showMaintenanceModal = false
            if (responseData.execution_id) {
              this.startExecutionProgress(responseData.execution_id)
            }
            this.$bvToast.toast(message, {
              title: 'AI Booking Re-shopping Engine',
              variant: responseData.execution_id ? 'info' : 'success',
              solid: true,
            })
            if (!responseData.execution_id) {
              this.currentPage = 1
              this.fetchBookings()
            }
            return
          }
          this.$bvToast.toast(message, {
            title: 'AI Booking Re-shopping Engine',
            variant: 'success',
            solid: true,
          })
          this.showMaintenanceModal = false
          this.currentPage = 1
          this.fetchBookings()
        })
        .catch(error => {
          const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Action failed.'
          const details = error.response && error.response.data && error.response.data.error
            ? ` ${error.response.data.error}`
            : ''
          this.$bvToast.toast(`${message}${details}`, {
            title: 'AI Booking Re-shopping Engine',
            variant: 'danger',
            solid: true,
          })
        })
        .finally(() => {
          this.isMaintenanceLoading = false
          this.maintenanceStartedAt = null
        })
    },
    resumeExecutionProgress() {
      const executionId = window.localStorage.getItem(EXECUTION_STORAGE_KEY)
      if (!executionId) {
        return
      }
      this.startExecutionProgress(executionId, { restored: true })
    },
    startExecutionProgress(executionId) {
      this.stopExecutionProgressPolling()
      this.executionRequestToken += 1
      this.executionId = executionId
      window.localStorage.setItem(EXECUTION_STORAGE_KEY, executionId)
      this.executionProgress = null
      this.executionStatus = 'starting'
      this.executionProgressError = ''
      this.executionCompletionNotified = false
      const requestToken = this.executionRequestToken
      this.fetchExecutionProgress(requestToken)
      this.executionPollTimer = window.setInterval(() => {
        this.fetchExecutionProgress(requestToken)
      }, 2000)
    },
    stopExecutionProgressPolling() {
      if (this.executionPollTimer) {
        window.clearInterval(this.executionPollTimer)
        this.executionPollTimer = null
      }
    },
    fetchExecutionProgress(requestToken = this.executionRequestToken) {
      if (!this.executionId || this.isExecutionPolling) {
        return
      }
      const executionId = this.executionId
      this.isExecutionPolling = true
      axios.get('price-check/script-progress/', {
        params: {
          execution_id: executionId,
        },
      }).then(response => {
        if (requestToken !== this.executionRequestToken || executionId !== this.executionId) {
          return
        }
        const progress = response.data && response.data.data ? response.data.data : response.data || {}
        this.executionProgress = progress
        this.executionStatus = progress.execution_status || this.executionStatus || 'running'
        this.executionProgressError = ''
        if (this.isTerminalExecution(progress)) {
          this.handleExecutionFinished(progress)
        }
      }).catch(error => {
        if (requestToken !== this.executionRequestToken || executionId !== this.executionId) {
          return
        }
        this.executionProgressError = this.getErrorMessage(error, 'Unable to fetch execution progress.')
      }).finally(() => {
        if (requestToken === this.executionRequestToken) {
          this.isExecutionPolling = false
        }
      })
    },
    isTerminalExecution(progress) {
      if (!progress) {
        return false
      }
      const status = String(progress.execution_status || this.executionStatus || '').trim().toLowerCase()
      return Boolean(progress.is_finished) || ['completed', 'completed_with_errors', 'failed'].includes(status)
    },
    handleExecutionFinished(progress) {
      this.stopExecutionProgressPolling()
      window.localStorage.removeItem(EXECUTION_STORAGE_KEY)
      if (this.executionCompletionNotified) {
        return
      }
      this.executionCompletionNotified = true
      const status = String(progress.execution_status || '').trim().toLowerCase()
      const lastError = progress.last_error ? ` ${progress.last_error}` : ''
      const variant = status === 'failed' ? 'danger' : status === 'completed_with_errors' ? 'warning' : 'success'
      const message = status === 'failed'
        ? `Price check execution failed.${lastError}`
        : status === 'completed_with_errors'
          ? `Price check execution completed with errors.${lastError}`
          : 'Price check execution completed successfully.'
      this.$bvToast.toast(message, {
        title: 'AI Booking Re-shopping Engine',
        variant,
        solid: true,
      })
      this.currentPage = 1
      this.fetchBookings()
      this.executionId = ''
      this.executionProgress = null
      this.executionStatus = ''
      this.executionProgressError = ''
    },
    fetchBookings() {
      this.isLoading = true
      this.nodata = false
      const params = {
        page: this.currentPage,
        page_size: this.pageSize,
        booking_id: this.filters.bookingId,
        city: this.filters.city,
        country: this.filters.country,
        client: this.filters.client,
        booking_date: this.filters.bookingDate,
        profit_range: this.filters.profitRange,
        refundability: this.filters.refundability,
      }
      if (this.filters.alternativePriceStatus) {
        params.alternative_price_status = this.filters.alternativePriceStatus
      }
      axios.get('price-check/bookings', {
        params,
      }).then(response => {
        this.bookings = response.data.results || []
        const pagination = response.data.pagination || {}
        this.totalRecords = pagination.total || 0
        this.totalPages = pagination.total_pages || 1
        this.nodata = this.bookings.length === 0
      }).catch(error => {
        console.error('Error fetching bookings:', error)
        this.bookings = []
        this.totalRecords = 0
        this.nodata = true
      }).finally(() => {
        this.isLoading = false
        this.loading = false
      })
    },
    changePageSize() {
      if (this.isLoading) {
        return
      }
      this.currentPage = 1
      this.fetchBookings()
    },
    firstPage() {
      if (!this.isLoading && this.currentPage !== 1) {
        this.currentPage = 1
        this.fetchBookings()
      }
    },
    lastPage() {
      if (!this.isLoading && this.currentPage !== this.totalPages) {
        this.currentPage = this.totalPages
        this.fetchBookings()
      }
    },
    applyFilters() {
      if (this.isLoading) {
        return
      }
      this.currentPage = 1
      this.fetchBookings()
    },
    resetFilters() {
      if (this.isLoading) {
        return
      }
      this.filters = {
        bookingId: '',
        city: '',
        country: '',
        client: '',
        alternativePriceStatus: '',
        bookingDate: '',
        profitRange: '',
        refundability: '',
      }
      this.showMoreFilters = false
      this.currentPage = 1
      this.fetchBookings()
    },
    previousPage() {
      if (!this.isLoading && this.currentPage > 1) {
        this.currentPage--
        this.fetchBookings()
      }
    },
    nextPage() {
      if (!this.isLoading && this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchBookings()
      }
    },
    goToPage(page) {
      if (!this.isLoading && this.isValidPage(page)) {
        this.currentPage = page
        this.fetchBookings()
      }
    },
    goToSpecificPage() {
      if (!this.isLoading && this.isValidPage(this.goToPageInput)) {
        this.currentPage = this.goToPageInput
        this.goToPageInput = null
        this.fetchBookings()
      }
    },
    isValidPage(page) {
      return page >= 1 && page <= this.totalPages
    },
    getRoomDetails(booking) {
      return Array.isArray(booking.room_details) ? booking.room_details : []
    },
    getPassengers(room) {
      return Array.isArray(room.passangers) ? room.passangers : []
    },
    roomTitle(room, index) {
      return room.RoomCategory || room.Room_TypeDescription || `Room ${index + 1}`
    },
    passengerText(passenger) {
      const type = passenger.PassengerType || 'Passenger'
      return passenger.Age || passenger.Age === 0
        ? `${type} - Age ${passenger.Age}`
        : type
    },
    isCheapPriceDetected(booking) {
      return String(booking.Price_status || '').toLowerCase() === 'cheaper_found'
    },
    formatDayTillCheckin(dayTillCheckin) {
      if (dayTillCheckin || dayTillCheckin === 0) {
        return `${dayTillCheckin} day${Number(dayTillCheckin) === 1 ? '' : 's'} till checkin`
      }
      return 'Day till checkin unavailable'
    },
    formatDisplayDate(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }

      const valueText = String(value).trim()
      const isoMatch = valueText.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
      }

      const slashMatch = valueText.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
      if (slashMatch) {
        const day = slashMatch[1].padStart(2, '0')
        const month = slashMatch[2].padStart(2, '0')
        return `${slashMatch[3]}-${month}-${day}`
      }

      return value
    },
    parseClickhouseDateTime(value) {
      if (!value) {
        return null
      }
      const normalizedValue = String(value).trim().replace(' ', 'T')
      const parsedDate = new Date(normalizedValue)
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
    },
    relativeTimeParts(value) {
      const targetDate = this.parseClickhouseDateTime(value)
      if (!targetDate) {
        return null
      }
      const diffMs = targetDate.getTime() - Date.now()
      const absMs = Math.abs(diffMs)
      const minute = 60 * 1000
      const hour = 60 * minute
      const day = 24 * hour
      if (absMs < minute) {
        return { amount: 0, unit: 'minute', isFuture: diffMs >= 0 }
      }
      if (absMs < hour) {
        return { amount: Math.round(absMs / minute), unit: 'minute', isFuture: diffMs >= 0 }
      }
      if (absMs < day) {
        return { amount: Math.round(absMs / hour), unit: 'hour', isFuture: diffMs >= 0 }
      }
      return { amount: Math.round(absMs / day), unit: 'day', isFuture: diffMs >= 0 }
    },
    formatRelativeNextCheck(value) {
      const parts = this.relativeTimeParts(value)
      if (!parts) {
        return value || '-'
      }
      if (parts.amount === 0) {
        return 'due now'
      }
      const unitLabel = `${parts.unit}${parts.amount === 1 ? '' : 's'}`
      return parts.isFuture
        ? `in ${parts.amount} ${unitLabel}`
        : `${parts.amount} ${unitLabel} overdue`
    },
    formatExactNextCheck(value) {
      return value ? `Next check: ${value}` : 'Next check unavailable'
    },
    toFiniteNumber(value) {
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : 0
    },
    getCompletedBookings(progress) {
      if (!progress) {
        return 0
      }
      if (progress.completed_bookings !== null && progress.completed_bookings !== undefined) {
        return this.toFiniteNumber(progress.completed_bookings)
      }
      return this.toFiniteNumber(progress.processed_bookings)
    },
    formatInteger(value) {
      return Math.round(this.toFiniteNumber(value)).toLocaleString('en-US')
    },
    clampPercentage(value) {
      const numericValue = this.toFiniteNumber(value)
      return Math.max(0, Math.min(100, Math.round(numericValue)))
    },
    formatStatusLabel(value) {
      return String(value || 'starting')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase())
    },
    getErrorMessage(error, fallback) {
      if (error.response && error.response.data) {
        return error.response.data.message || error.response.data.error || fallback
      }
      return fallback
    },
    checkBooking(booking) {
      const bookingId = String(booking.BookingId || '').trim()
      if (!bookingId) {
        return
      }

      const route = this.$router.resolve({
        name: 'priceCheckBookingResult',
        params: {
          bookingId: bookingId,
        },
      })
      window.open(route.href, '_blank', 'noopener')
    },
    goToReports() {
      this.$router.push({ name: 'priceCheckBookingReports' })
    },
  },
}
</script>

<style scoped>
.price-check-page {
  background: #f6f8fb;
  min-height: calc(100vh - 70px);
  padding: 18px 18px 28px;
}

.page-header {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-header h3 {
  color: #172033;
  font-size: 25px;
  font-weight: 700;
  margin: 0 0 4px;
}

.page-header p {
  color: #65758b;
  font-size: 14px;
  margin: 0;
}

.page-actions {
  align-items: center;
  background: #f8fbff;
  border: 1px solid #dce7f4;
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  padding: 8px;
}

.page-actions :deep(.btn) {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  font-size: 13px;
  font-weight: 800;
  gap: 7px;
  height: 38px;
  justify-content: center;
  line-height: 1;
  min-width: 116px;
  padding: 0 13px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease, transform 0.18s ease;
  white-space: nowrap;
}

.page-actions :deep(.btn svg) {
  height: 15px;
  width: 15px;
}

.page-actions :deep(.btn:not(:disabled):hover) {
  box-shadow: 0 8px 18px rgba(22, 32, 51, 0.12);
  transform: translateY(-1px);
}

.page-actions :deep(.btn:focus) {
  box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.14);
}

.page-actions :deep(.action-button-danger) {
  background: #fff;
  border-color: #f3b6bd;
  color: #c92235;
}

.page-actions :deep(.action-button-danger:not(:disabled):hover) {
  background: #fff3f4;
  border-color: #e75b69;
  color: #a91526;
}

.page-actions :deep(.action-button-secondary) {
  background: #fff;
  border-color: #bfd7f6;
  color: #0f67d8;
}

.page-actions :deep(.action-button-secondary:not(:disabled):hover) {
  background: #eff6ff;
  border-color: #6ba7ef;
  color: #0757bd;
}

.page-actions :deep(.action-button-primary) {
  background: linear-gradient(180deg, #2f80ed 0%, #1169de 100%);
  border-color: #1169de;
  box-shadow: 0 5px 14px rgba(17, 105, 222, 0.24);
  color: #fff;
}

.page-actions :deep(.action-button-primary:not(:disabled):hover) {
  background: linear-gradient(180deg, #2477e7 0%, #075fcf 100%);
  border-color: #075fcf;
  color: #fff;
}

.execution-progress-panel {
  background: #fff;
  border: 1px solid #d6e4f5;
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(22, 32, 51, 0.08);
  margin-bottom: 16px;
  padding: 18px 20px;
}

.execution-progress-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.execution-kicker {
  color: #6b7c93;
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.execution-progress-header h4 {
  color: #172033;
  font-size: 18px;
  font-weight: 800;
  margin: 0;
}

.execution-status-chip {
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 12px;
  white-space: nowrap;
}

.status-running .execution-status-chip {
  background: #eaf3ff;
  border: 1px solid #b7d4fb;
  color: #0f67d8;
}

.status-completed .execution-status-chip {
  background: #e8fff4;
  border: 1px solid #95e0bc;
  color: #007a48;
}

.status-warning .execution-status-chip {
  background: #fff7e6;
  border: 1px solid #ffd37a;
  color: #9a6200;
}

.status-failed .execution-status-chip {
  background: #fff0f2;
  border: 1px solid #f5aab4;
  color: #b32335;
}

.execution-progress-main {
  margin-bottom: 16px;
}

.execution-progress-copy {
  align-items: baseline;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 8px;
}

.execution-progress-copy strong {
  color: #172033;
  font-size: 22px;
  font-weight: 900;
}

.execution-progress-copy span {
  color: #53657d;
  font-size: 13px;
  font-weight: 700;
  text-align: right;
}

.execution-progress-track {
  background: #edf3fa;
  border-radius: 999px;
  height: 12px;
  overflow: hidden;
}

.execution-progress-fill {
  background: linear-gradient(90deg, #1769e0 0%, #20b486 100%);
  border-radius: inherit;
  height: 100%;
  min-width: 3px;
  transition: width 0.35s ease;
}

.status-failed .execution-progress-fill {
  background: linear-gradient(90deg, #d82f45 0%, #f0717f 100%);
}

.status-warning .execution-progress-fill {
  background: linear-gradient(90deg, #e39a10 0%, #f6c85f 100%);
}

.execution-metrics-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  margin-bottom: 14px;
}

.execution-metric {
  background: #f8fbff;
  border: 1px solid #dce7f4;
  border-radius: 8px;
  padding: 10px 12px;
}

.execution-metric span {
  color: #64748b;
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 5px;
}

.execution-metric strong {
  color: #172033;
  display: block;
  font-size: 18px;
  font-weight: 900;
}

.execution-clients h5 {
  color: #172033;
  font-size: 14px;
  font-weight: 800;
  margin: 16px 0 10px;
}

.execution-client {
  border: 1px solid #dce7f4;
  border-radius: 8px;
  margin-top: 10px;
  padding: 12px;
}

.execution-client .execution-progress-copy strong {
  font-size: 14px;
  overflow-wrap: anywhere;
}

.execution-client-counts {
  color: #53657d;
  font-size: 12px;
  margin-top: 8px;
}

.execution-error {
  background: #fff4f5;
  border: 1px solid #f4b8c0;
  border-radius: 8px;
  color: #a61b2d;
  font-size: 13px;
  font-weight: 700;
  margin-top: 12px;
  padding: 10px 12px;
}

.filter-panel {
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  box-shadow: 0 8px 18px rgba(22, 32, 51, 0.05);
  margin-bottom: 16px;
  padding: 18px 20px;
}

.filter-grid {
  align-items: end;
  display: grid;
  gap: 16px 14px;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
}

.filter-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.filter-field label {
  color: #253247;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 7px;
}

.filter-field input,
.filter-field select {
  background: #fbfdff;
  border: 1px solid #cbd7e5;
  border-radius: 6px;
  color: #172033;
  font-size: 14px;
  height: 40px;
  outline: none;
  padding: 8px 12px;
  width: 100%;
}

.filter-field input:focus,
.filter-field select:focus {
  border-color: #378ad6;
  box-shadow: 0 0 0 3px rgba(55, 138, 214, 0.12);
}

.filter-toggle,
.filter-actions {
  align-items: end;
  display: flex;
  gap: 8px;
  min-width: 0;
}

.filter-toggle :deep(.btn),
.filter-actions :deep(.btn) {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  gap: 6px;
  justify-content: center;
  font-weight: 600;
  height: 40px;
  min-width: 88px;
}

.filter-toggle :deep(.btn) {
  width: 100%;
}

.filter-actions {
  grid-column: 1 / -1;
  justify-content: flex-end;
}

.filter-actions-expanded {
  grid-column: span 3;
  justify-content: flex-start;
}

.initial-loader {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(22, 32, 51, 0.06);
  color: #172033;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 260px;
  padding: 34px 18px;
  text-align: center;
}

.initial-loader h5 {
  font-size: 18px;
  font-weight: 700;
  margin: 16px 0 6px;
}

.initial-loader p {
  color: #65758b;
  font-size: 14px;
  margin: 0;
}

.loader-ring {
  animation: loader-spin 0.85s linear infinite;
  border: 4px solid #dceafb;
  border-top-color: #378ad6;
  border-radius: 50%;
  height: 46px;
  width: 46px;
}

.loader-ring.small {
  border-width: 3px;
  height: 26px;
  width: 26px;
}

.booking-panel {
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(22, 32, 51, 0.06);
  padding: 14px 16px 18px;
  position: relative;
}

.panel-loader {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid #d7e7f8;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(22, 32, 51, 0.12);
  color: #1d3553;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 10px;
  left: 50%;
  padding: 12px 16px;
  position: absolute;
  top: 72px;
  transform: translateX(-50%);
  z-index: 4;
}

.table-tools {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.result-summary {
  align-items: center;
  color: #64748b;
  display: flex;
  gap: 12px;
  font-size: 13px;
}

.result-count {
  align-items: baseline;
  display: flex;
  gap: 6px;
}

.result-summary strong {
  color: #172033;
  font-size: 20px;
}

.reports-button {
  border-radius: 6px;
  font-weight: 700;
  min-width: 82px;
}

.page-size-control {
  align-items: center;
  display: flex;
  gap: 8px;
}

.page-size-control label {
  font-size: 13px;
  font-weight: 700;
  margin: 0;
}

.page-size-control select,
.page-size-control :deep(.custom-select) {
  border-radius: 8px;
  height: 36px;
  min-width: 88px;
}

.table-spinner {
  display: block;
  margin: 40px auto;
}

.table-wrap {
  border: 1px solid #e1e8f0;
  border-radius: 8px;
  overflow-x: auto;
  transition: opacity 0.2s ease;
}

.table-wrap.refreshing {
  opacity: 0.42;
  pointer-events: none;
}

.booking-table {
  background: #fff;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  width: 100%;
}

.booking-table thead th {
  background: #edf5fd;
  border-bottom: 1px solid #d8e5f1;
  color: #213047;
  font-size: 12px;
  font-weight: 800;
  padding: 13px 14px;
  text-transform: uppercase;
  white-space: nowrap;
}

.booking-table tbody td {
  border-bottom: 1px solid #edf1f5;
  color: #27364a;
  font-size: 14px;
  padding: 13px 14px;
  vertical-align: middle;
  white-space: nowrap;
}

.booking-table tbody tr:hover {
  background: #f8fbff;
}

.booking-table tbody tr:last-child td {
  border-bottom: 0;
}
.booking-table tbody tr.cheap-price-row td {
  background: #e7f8ee;
  color: #173f2a;
}

.booking-table tbody tr.cheap-price-row:hover td {
  background: #d6f1e1;
}

.booking-table tbody tr.cheap-price-row .booking-id,
.booking-table tbody tr.cheap-price-row .price-cell {
  color: #0d3a23;
}

.date-popover-target {
  border-bottom: 1px dashed #6b7a90;
  cursor: help;
  display: inline-block;
}

.next-check-value {
  background: #eef6ff;
  border: 1px solid #cfe3fb;
  border-radius: 999px;
  color: #175a9f;
  cursor: help;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  padding: 6px 9px;
}


.booking-id,
.price-cell {
  color: #122238;
  font-weight: 700;
}

.booking-id span,
.booking-id .room-info-icon {
  vertical-align: middle;
}

.booking-id .room-info-icon {
  margin-left: 8px;
}

.room-info-icon {
  color: #2f80ed;
  cursor: help;
  font-size: 15px;
}

.room-details-list {
  display: grid;
  gap: 10px;
  max-width: 320px;
  max-height: 300px;
  overflow: auto;
}

.room-details-item {
  border-bottom: 1px solid #e5edf6;
  color: #27364a;
  padding-bottom: 9px;
}

.room-details-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.room-details-item strong,
.room-details-item span,
.room-details-item small {
  display: block;
}

.room-details-item strong {
  color: #122238;
  font-size: 13px;
  margin-bottom: 3px;
}

.room-details-item span,
.room-details-item small,
.room-details-item li {
  color: #52657d;
  font-size: 12px;
  line-height: 1.4;
}

.room-details-item ul {
  margin: 6px 0 0;
  padding-left: 16px;
}

.check-btn {
  border-radius: 8px;
  font-weight: 700;
  min-width: 74px;
}

.maintenance-confirm {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  padding: 8px 2px 2px;
}

.maintenance-icon {
  align-items: center;
  border-radius: 14px;
  display: inline-flex;
  flex: 0 0 52px;
  font-size: 24px;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.maintenance-icon.danger {
  background: #fef2f2;
  color: #dc2626;
}

.maintenance-icon.primary {
  background: #eff6ff;
  color: #2563eb;
}

.maintenance-icon.success {
  background: #ecfdf5;
  color: #059669;
}

.maintenance-copy span {
  color: #64748b;
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.maintenance-copy h4 {
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 8px;
}

.maintenance-copy p {
  color: #52657d;
  font-size: 14px;
  line-height: 1.55;
  margin: 0;
}

.maintenance-progress {
  margin-top: 18px;
}

.maintenance-progress-header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 9px;
}

.maintenance-progress-header strong {
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
}

.maintenance-progress-header span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.maintenance-progress-track {
  background: #e8eef7;
  border-radius: 999px;
  height: 10px;
  overflow: hidden;
  position: relative;
}

.maintenance-progress-bar {
  animation: maintenance-progress-slide 1.1s ease-in-out infinite;
  background: linear-gradient(90deg, #16a34a 0%, #4ade80 45%, #0ea5e9 100%);
  border-radius: inherit;
  height: 100%;
  left: -45%;
  position: absolute;
  top: 0;
  width: 45%;
}

@keyframes maintenance-progress-slide {
  0% {
    left: -45%;
  }

  100% {
    left: 100%;
  }
}

.modal-cancel-button,
.modal-confirm-button {
  border-radius: 8px;
  font-weight: 700;
  min-width: 86px;
  padding: 8px 16px;
}

:deep(.maintenance-modal-body) {
  padding: 26px 28px 18px;
}

:deep(.maintenance-modal-footer) {
  background: #ffffff;
  border-top: 0;
  gap: 10px;
  justify-content: center;
  padding: 12px 28px 26px;
}

:deep(.modal-content) {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 22px 70px rgba(15, 23, 42, 0.24);
  overflow: hidden;
}

@keyframes loader-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1100px) {
  .filter-grid {
    grid-template-columns: repeat(3, minmax(160px, 1fr));
  }
}

@media (max-width: 640px) {
  .price-check-page {
    padding: 12px;
  }

  .page-header {
    align-items: stretch;
    flex-direction: column;
  }

  .page-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .page-actions :deep(.btn) {
    flex: 1 1 180px;
  }

  .execution-progress-header,
  .execution-progress-copy {
    align-items: flex-start;
    flex-direction: column;
  }

  .execution-progress-copy span {
    text-align: left;
  }

  .execution-metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    grid-column: 1;
  }

  .filter-toggle :deep(.btn),
  .filter-actions :deep(.btn) {
    width: 100%;
  }

  .filter-actions,
  .table-tools {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
